import { useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Divider,
  Grid,
  TextField,
  Button,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { Details } from './Details';
import type { SquaresAlert } from 'types/squares.type';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const UnExpectedDowntimeView = ({
  enableDarkTheme,
  notification,
}: {
  enableDarkTheme: boolean;
  notification: SquaresAlert;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const fontColor = palette.textPrimary;

  const [maintenanceType, setMaintenanceType] = useState('preventive');
  const [selectedMilestone, setSelectedMilestone] = useState('750');

  const milestones = [
    { label: t('375hr_pm'), value: '375' },
    { label: '750HR PM', value: '750' },
    { label: t('1500hr_pm'), value: '1500' },
  ];

  const handleMaintenanceChange = (event: any) => {
    const value = event.target.value;
    setMaintenanceType(value);
    if (value !== 'preventive') {
      setSelectedMilestone('');
    } else {
      setSelectedMilestone('750');
    }
  };

  const handleMilestoneClick = (value: any) => {
    if (maintenanceType === 'preventive') {
      setSelectedMilestone(value);
    }
  };
  return (
    <Box>
      <Details enableDarkTheme={enableDarkTheme} notification={notification} />
      <Box
        sx={{
          marginTop: '10px',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            color: ThemePalette.typography.grey,
            fontWeight: '600',
          }}
        >
          {t('description')}
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            color: fontColor,
            marginTop: '10px',
          }}
          dangerouslySetInnerHTML={{ __html: notification.message }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: '14px',
          color: ThemePalette.typography.grey,
          fontWeight: '600',
          marginTop: '20px',
        }}
      >
        {t('please_share_the_feedback')}
      </Typography>
      <Box
        sx={{
          marginTop: '10px',
          borderRadius: '4px',
          border: '1px solid #2F445E',
          padding: '10px',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: ThemePalette.typography.grey,
            fontWeight: '700',
          }}
        >
          {t('was_maintenance_performed')}
        </Typography>

        <Box>
          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <RadioGroup
              value={maintenanceType}
              onChange={handleMaintenanceChange}
            >
              {/* Preventive Maintenance */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  },
                }}
              >
                <FormControlLabel
                  value="preventive"
                  control={
                    <Radio
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{ color: 'white', fontWeight: 500 }}
                    >
                      {t('performed_preventive_maintenance')}
                    </Typography>
                  }
                  sx={{ mr: 0 }}
                />

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {milestones.map((milestone) => (
                    <Chip
                      key={milestone.value}
                      label={milestone.label}
                      onClick={() => {
                        handleMilestoneClick(milestone.value);
                      }}
                      disabled={maintenanceType !== 'preventive'}
                      variant={
                        maintenanceType === 'preventive' &&
                        selectedMilestone === milestone.value
                          ? 'filled'
                          : 'outlined'
                      }
                      sx={{
                        bgcolor:
                          maintenanceType === 'preventive' &&
                          selectedMilestone === milestone.value
                            ? 'primary.main'
                            : 'transparent',
                        color:
                          maintenanceType === 'preventive'
                            ? 'white'
                            : 'rgba(255, 255, 255, 0.3)',
                        borderColor:
                          maintenanceType === 'preventive'
                            ? selectedMilestone === milestone.value
                              ? 'primary.main'
                              : 'rgba(255, 255, 255, 0.3)'
                            : 'rgba(255, 255, 255, 0.12)',
                        fontWeight:
                          selectedMilestone === milestone.value ? 600 : 400,
                        minWidth: 110,
                        cursor:
                          maintenanceType === 'preventive'
                            ? 'pointer'
                            : 'not-allowed',
                        '&:hover': {
                          bgcolor:
                            maintenanceType === 'preventive'
                              ? selectedMilestone === milestone.value
                                ? '#1565c0'
                                : 'rgba(255, 255, 255, 0.05)'
                              : 'transparent',
                        },
                        '&.Mui-disabled': {
                          opacity: 0.5,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Corrective Maintenance */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  },
                }}
              >
                <FormControlLabel
                  value="corrective"
                  control={
                    <Radio
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{ color: 'white', fontWeight: 500 }}
                    >
                      {t('performed_corrective_maintenance')}
                    </Typography>
                  }
                />
              </Box>

              {/* No Maintenance */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  },
                }}
              >
                <FormControlLabel
                  value="none"
                  control={
                    <Radio
                      sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      sx={{ color: 'white', fontWeight: 500 }}
                    >
                      {t('no_maintenance_done')}
                    </Typography>
                  }
                />
              </Box>
            </RadioGroup>
          </FormControl>
        </Box>

        {notification.data.length > 0 && (
          <>
            <Divider
              sx={{
                backgroundColor: palette.cardBorderColor,
                marginTop: '10px',
                marginBottom: '10px',
              }}
            />
            <Grid container spacing={2}>
              <Grid item lg={6} md={12}>
                <Box
                  sx={{
                    padding: '10px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: ThemePalette.typography.grey,
                      fontWeight: '600',
                    }}
                  >
                    <span
                      style={{
                        marginRight: '5px',
                        fontSize: '16px',
                        fontWeight: '700',
                        textDecoration: 'underline',
                      }}
                    >
                      {notification.data[0].work_order_number}
                    </span>
                    {notification.data[0].description}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      marginTop: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        color: fontColor,
                        marginTop: '10px',
                      }}
                    >
                      {t('date')} {notification.data[0].date_of_orders}
                    </Typography>
                    <FormControl
                      sx={{
                        marginLeft: '20px',
                      }}
                    >
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                      >
                        <FormControlLabel
                          value="yes"
                          control={
                            <Radio
                              sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                '&.Mui-checked': {
                                  color: 'primary.main',
                                },
                              }}
                            />
                          }
                          sx={{
                            color: fontColor,
                          }}
                          label={t('yes')}
                        />
                        <FormControlLabel
                          value="no"
                          control={
                            <Radio
                              sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                '&.Mui-checked': {
                                  color: 'primary.main',
                                },
                              }}
                            />
                          }
                          sx={{
                            color: fontColor,
                          }}
                          label={t('no')}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  <Typography
                    sx={{
                      marginTop: '10px',
                      fontWeight: '700',
                      color: fontColor,
                    }}
                  >
                    {t('performed_corrective_maintenance')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item lg={6} md={12}>
                <Box
                  sx={{
                    border: '1px solid #2F445E',
                    padding: '10px 10px 10px 20px',
                    borderRadius: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: ThemePalette.typography.grey,
                      fontWeight: '600',
                    }}
                  >
                    {t('did_you_perform_this_corrective_work')}
                  </Typography>
                  <FormControl
                    sx={{
                      marginTop: '10px',
                    }}
                  >
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                    >
                      <FormControlLabel
                        value="yes"
                        control={
                          <Radio
                            sx={{
                              color: 'rgba(255, 255, 255, 0.5)',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        sx={{
                          color: fontColor,
                        }}
                        label={t('yes')}
                      />
                      <FormControlLabel
                        value="no"
                        control={
                          <Radio
                            sx={{
                              color: 'rgba(255, 255, 255, 0.5)',
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        sx={{
                          color: fontColor,
                        }}
                        label={t('no')}
                      />
                    </RadioGroup>
                  </FormControl>
                  <TextField
                    value={''}
                    onChange={(e) => {
                      console.log(e.target.value);
                    }}
                    variant="outlined"
                    size="small"
                    placeholder={t(
                      'please_provide_details_of_the_maintenance_done_during_this_t'
                    )}
                    multiline
                    minRows={2}
                    maxRows={6}
                    InputProps={{
                      sx: {
                        color: enableDarkTheme
                          ? ThemePalette.typography.wheat
                          : ThemePalette.typography.black,
                        '& textarea': {
                          fontSize: '14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#38475E',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#38475E',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#38475E',
                        },
                        borderRadius: '4px',
                        marginTop: '10px',
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        color: enableDarkTheme
                          ? ThemePalette.typography.wheat
                          : ThemePalette.typography.black,
                        '&.Mui-focused': {
                          color: enableDarkTheme
                            ? ThemePalette.typography.wheat
                            : ThemePalette.typography.black,
                        },
                      },
                    }}
                    fullWidth
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            sx={{
              marginTop: '10px',
              textTransform: 'none',
              fontSize: '12px',
              backgroundColor: '#1565c0',
              color: ThemePalette.typography.white,
              border: '1px solid #2F445E',
              padding: '6px 20px',
            }}
          >
            {t('submit_feedback')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
